import React, { useCallback, useEffect, useState } from "react";
import {
  Button,
  Form,
  Input,
  InputNumber,
  Modal,
  Select,
  Space,
  Switch,
  Table,
  Tag,
  message,
} from "antd";
import { useSelector } from "react-redux";

import { ApiManager, Locale, Request } from "../../utils";
import { getUserInfo } from "../../utils/redux/reducers/User";
import TranslateBtn from "../../components/product/TranslateBtn";
import Base from "../Base";

const api = ApiManager.getInstance();
const i18n = Locale.getInstance();
const base = Base.getInstance();
const { TextArea } = Input;

const TRIGGERS = [
  { value: "first_paid_order", labelKey: "task.triggerFirstPaidOrder" },
  { value: "upload_avatar", labelKey: "task.triggerUploadAvatar" },
  { value: "update_nickname", labelKey: "task.triggerUpdateNickname" },
];

const VISIBILITIES = [
  { value: "silent", labelKey: "task.visibilitySilent" },
  { value: "visible", labelKey: "task.visibilityVisible" },
];

function TasksList() {
  const user = useSelector((state) => getUserInfo(state));
  const [form] = Form.useForm();
  const [listLoading, setListLoading] = useState(false);
  const [listPayload, setListPayload] = useState(null);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [cards, setCards] = useState([]);
  const [coupons, setCoupons] = useState([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const loadCards = useCallback(async () => {
    if (!user?.token) return;
    try {
      const res = await Request.get(
        "/member/api/v6/merchant/member-card",
        user.token
      );
      if (res.result && res.data?.data) {
        setCards(res.data.data);
      }
    } catch (e) {
      message.error(e.message);
    }
  }, [user?.token]);

  const loadCoupons = useCallback(async () => {
    if (!user?.token) return;
    try {
      const res = await api.coupons.list.run({
        token: user.token,
        query: { count: 200 },
      });
      if (res.result) {
        setCoupons(res.data?.data || []);
      }
    } catch (e) {
      // Coupons optional for some merchants
    }
  }, [user?.token]);

  const loadTasks = useCallback(async () => {
    if (!user?.token) return;
    setListLoading(true);
    try {
      const res = await api.task.list.run({
        token: user.token,
        query: { count: pageSize, page },
      });
      if (res.result) {
        setListPayload(res.data);
      } else {
        message.warning(res.message);
      }
    } catch (e) {
      base.handleApiError(api.task.list, e);
    } finally {
      setListLoading(false);
    }
  }, [user?.token, page, pageSize]);

  useEffect(() => {
    loadCards();
    loadCoupons();
  }, [loadCards, loadCoupons]);

  useEffect(() => {
    loadTasks();
  }, [loadTasks]);

  const openCreate = () => {
    setEditingId(null);
    form.resetFields();
    form.setFieldsValue({
      visibility: "silent",
      trigger: "first_paid_order",
      enabled: true,
      sort: 0,
      coupon_ids: [],
    });
    setModalOpen(true);
  };

  const openEdit = (record) => {
    setEditingId(record.id);
    const rewards = record.rewards || {};
    form.setFieldsValue({
      ug_name: record.ug_name,
      zh_name: record.zh_name,
      ug_description: record.ug_description,
      zh_description: record.zh_description,
      trigger: record.trigger,
      visibility: record.visibility,
      enabled: !!record.enabled,
      sort: record.sort || 0,
      member_card_id: rewards.member_card_id
        ? String(rewards.member_card_id)
        : undefined,
      coupon_ids: (rewards.coupon_ids || []).map(String),
    });
    setModalOpen(true);
  };

  const closeModal = () => {
    setModalOpen(false);
    setEditingId(null);
    form.resetFields();
  };

  const buildPayload = (values) => {
    const memberCardId = values.member_card_id
      ? Number(values.member_card_id)
      : null;
    const couponIds = (values.coupon_ids || [])
      .map((id) => Number(id))
      .filter((id) => id > 0);
    return {
      ug_name: values.ug_name || "",
      zh_name: values.zh_name || "",
      ug_description: values.ug_description || "",
      zh_description: values.zh_description || "",
      trigger: values.trigger,
      visibility: values.visibility,
      enabled: !!values.enabled,
      sort: Number(values.sort) || 0,
      rewards: {
        member_card_id: memberCardId || null,
        coupon_ids: couponIds,
      },
    };
  };

  const onSubmit = async () => {
    try {
      const values = await form.validateFields();
      const payload = buildPayload(values);
      if (
        !payload.rewards.member_card_id &&
        (!payload.rewards.coupon_ids || payload.rewards.coupon_ids.length === 0)
      ) {
        message.warning(i18n.t("task.rewardRequired"));
        return;
      }
      setSubmitting(true);
      const res = editingId
        ? await api.task.update.run({
            token: user.token,
            path: { id: editingId },
            data: payload,
          })
        : await api.task.create.run({
            token: user.token,
            data: payload,
          });
      if (res.result) {
        message.success(res.message || i18n.t("common.saveSuccess"));
        closeModal();
        loadTasks();
      } else {
        message.warning(res.message);
      }
    } catch (e) {
      if (e?.errorFields) return;
      base.handleApiError(editingId ? api.task.update : api.task.create, e);
    } finally {
      setSubmitting(false);
    }
  };

  const onDelete = (record) => {
    base.showModal(
      i18n.t("sales.deleteWarning"),
      Base.Modal.confirm,
      async () => {
        try {
          const res = await api.task.delete.run({
            token: user.token,
            path: { id: record.id },
          });
          if (res.result) {
            loadTasks();
          } else {
            message.warning(res.message);
          }
        } catch (e) {
          base.handleApiError(api.task.delete, e);
        }
      }
    );
  };

  const triggerLabel = (value) => {
    const item = TRIGGERS.find((t) => t.value === value);
    return item ? i18n.t(item.labelKey) : value;
  };

  const visibilityLabel = (value) => {
    const item = VISIBILITIES.find((t) => t.value === value);
    return item ? i18n.t(item.labelKey) : value;
  };

  const columns = [
    { title: "ID", dataIndex: "id", width: 70 },
    {
      title: i18n.t("agentLevel.zh"),
      dataIndex: "zh_name",
      width: 140,
    },
    {
      title: i18n.t("agentLevel.ug"),
      dataIndex: "ug_name",
      width: 140,
      render: (text) => (
        <span style={{ fontFamily: "Alp Ekran" }}>{text}</span>
      ),
    },
    {
      title: i18n.t("task.trigger"),
      dataIndex: "trigger",
      width: 140,
      render: (v) => triggerLabel(v),
    },
    {
      title: i18n.t("task.visibility"),
      dataIndex: "visibility",
      width: 110,
      render: (v) => (
        <Tag color={v === "silent" ? "default" : "blue"}>
          {visibilityLabel(v)}
        </Tag>
      ),
    },
    {
      title: i18n.t("task.status"),
      dataIndex: "enabled",
      width: 90,
      render: (v) => (
        <Tag color={v ? "success" : "default"}>
          {v ? i18n.t("task.enabled") : i18n.t("task.disabled")}
        </Tag>
      ),
    },
    {
      title: i18n.t("task.sort"),
      dataIndex: "sort",
      width: 80,
    },
    {
      title: i18n.t("product.cats.actions"),
      width: 180,
      render: (_, record) => (
        <Space size="small">
          <Button type="link" size="small" onClick={() => openEdit(record)}>
            {i18n.t("product.productItem.actionEdit")}
          </Button>
          <Button type="link" size="small" danger onClick={() => onDelete(record)}>
            {i18n.t("product.productItem.actionDelete")}
          </Button>
        </Space>
      ),
    },
  ];

  return (
    <>
      <Button type="primary" style={{ marginBottom: 16 }} onClick={openCreate}>
        {i18n.t("task.add")}
      </Button>
      <Table
        loading={listLoading}
        dataSource={listPayload?.data || []}
        columns={columns}
        rowKey={(r) => `task-${r.id}`}
        scroll={{ x: 900 }}
        pagination={{
          current: page,
          pageSize,
          total: listPayload?.total || 0,
          onChange: (p, ps) => {
            setPage(p);
            setPageSize(ps);
          },
        }}
      />
      <Modal
        title={editingId ? i18n.t("task.edit") : i18n.t("task.add")}
        open={modalOpen}
        onCancel={closeModal}
        onOk={onSubmit}
        confirmLoading={submitting}
        width={640}
        destroyOnClose
      >
        <Form form={form} layout="vertical">
          <Form.Item noStyle shouldUpdate>
            {({ getFieldValue, setFieldsValue }) => (
              <Form.Item
                name="zh_name"
                label={i18n.t("agentLevel.zh")}
                rules={[{ required: true, message: i18n.t("task.nameRequired") }]}
              >
                <Input
                  allowClear
                  addonAfter={
                    <TranslateBtn
                      context={getFieldValue("zh_name")}
                      onFinish={(value) => setFieldsValue({ ug_name: value })}
                      from="zh"
                      to="ug"
                    />
                  }
                />
              </Form.Item>
            )}
          </Form.Item>
          <Form.Item noStyle shouldUpdate>
            {({ getFieldValue, setFieldsValue }) => (
              <Form.Item
                name="ug_name"
                label={i18n.t("agentLevel.ug")}
                rules={[{ required: true, message: i18n.t("task.nameRequired") }]}
              >
                <Input
                  allowClear
                  dir="rtl"
                  style={{ fontFamily: "Alp Ekran" }}
                  addonAfter={
                    <TranslateBtn
                      context={getFieldValue("ug_name")}
                      onFinish={(value) => setFieldsValue({ zh_name: value })}
                      from="ug"
                      to="zh"
                    />
                  }
                />
              </Form.Item>
            )}
          </Form.Item>
          <Form.Item noStyle shouldUpdate>
            {({ getFieldValue, setFieldsValue }) => (
              <Form.Item
                name="zh_description"
                label={
                  <Space>
                    {i18n.t("member.zhDesc")}
                    <TranslateBtn
                      context={getFieldValue("zh_description")}
                      onFinish={(value) =>
                        setFieldsValue({ ug_description: value })
                      }
                      from="zh"
                      to="ug"
                    />
                  </Space>
                }
              >
                <TextArea rows={2} allowClear />
              </Form.Item>
            )}
          </Form.Item>
          <Form.Item noStyle shouldUpdate>
            {({ getFieldValue, setFieldsValue }) => (
              <Form.Item
                name="ug_description"
                label={
                  <Space>
                    {i18n.t("member.ugDesc")}
                    <TranslateBtn
                      context={getFieldValue("ug_description")}
                      onFinish={(value) =>
                        setFieldsValue({ zh_description: value })
                      }
                      from="ug"
                      to="zh"
                    />
                  </Space>
                }
              >
                <TextArea
                  rows={2}
                  allowClear
                  dir="rtl"
                  style={{ fontFamily: "Alp Ekran" }}
                />
              </Form.Item>
            )}
          </Form.Item>
          <Form.Item
            name="trigger"
            label={i18n.t("task.trigger")}
            rules={[{ required: true }]}
          >
            <Select
              options={TRIGGERS.map((t) => ({
                value: t.value,
                label: i18n.t(t.labelKey),
              }))}
            />
          </Form.Item>
          <Form.Item
            name="visibility"
            label={i18n.t("task.visibility")}
            rules={[{ required: true }]}
            extra={i18n.t("task.visibilityHint")}
          >
            <Select
              options={VISIBILITIES.map((t) => ({
                value: t.value,
                label: i18n.t(t.labelKey),
              }))}
            />
          </Form.Item>
          <Form.Item name="member_card_id" label={i18n.t("task.memberCard")}>
            <Select
              allowClear
              placeholder={i18n.t("task.memberCardPlaceholder")}
              options={(cards || []).map((card) => ({
                value: String(card.id),
                label: `${card.id} · ${i18n.getPropNameByLocale(card)} · ${
                  card.discount_percent
                }%`,
              }))}
            />
          </Form.Item>
          <Form.Item name="coupon_ids" label={i18n.t("task.coupons")}>
            <Select
              mode="multiple"
              allowClear
              placeholder={i18n.t("task.couponsPlaceholder")}
              options={(coupons || []).map((c) => ({
                value: String(c.id),
                label: `${c.id} · ${i18n.getPropNameByLocale(c)}`,
              }))}
            />
          </Form.Item>
          <Form.Item
            name="enabled"
            label={i18n.t("task.status")}
            valuePropName="checked"
          >
            <Switch
              checkedChildren={i18n.t("task.enabled")}
              unCheckedChildren={i18n.t("task.disabled")}
            />
          </Form.Item>
          <Form.Item name="sort" label={i18n.t("task.sort")}>
            <InputNumber style={{ width: "100%" }} />
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
}

export default TasksList;
